import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth-context'
import { api, type SandboxDemoPersona } from '../lib/api'
import { getDefaultRouteForUser } from '../lib/role-routing'

const PERSONA_COPY: Record<string, { title: string; description: string; action: string }> = {
  platformOwner: {
    title: 'Platform Owner',
    description: 'Open support controls and multi-company audit views.',
    action: 'Enter platform owner sandbox',
  },
  dealerTransportCoordinator: {
    title: 'Dealer Transport Coordinator',
    description: 'Run the dispatch workspace and assign active movement.',
    action: 'Enter dealer dispatch sandbox',
  },
  dealerSalesRequestor: {
    title: 'Dealer Sales Requestor',
    description: 'Review request and order visibility from sales context.',
    action: 'Enter dealer sales sandbox',
  },
  dealerAccounting: {
    title: 'Dealer Accounting',
    description: 'Inspect payables, invoices, and settlement context.',
    action: 'Enter accounting sandbox',
  },
  dealerStoreManager: {
    title: 'Dealer Store Manager',
    description: 'Track readiness and delivery outcomes for store operations.',
    action: 'Enter store manager sandbox',
  },
  carrierOwnerDispatcher: {
    title: 'Carrier Owner / Dispatcher',
    description: 'Review carrier assignment execution and dispatch timeline.',
    action: 'Enter carrier dispatch sandbox',
  },
  carrierOperations: {
    title: 'Carrier Operations',
    description: 'View operations-level trip movement and load state.',
    action: 'Enter carrier operations sandbox',
  },
  carrierDriver: {
    title: 'Carrier Driver',
    description: 'Open driver trip tools, inspections, and proof workflow.',
    action: 'Enter driver sandbox',
  },
}

function personaLabel(persona: SandboxDemoPersona) {
  return PERSONA_COPY[persona.key]?.title ?? persona.name
}

function personaDescription(persona: SandboxDemoPersona) {
  return PERSONA_COPY[persona.key]?.description ?? `${persona.accountType} sandbox role`
}

function personaAction(persona: SandboxDemoPersona) {
  return PERSONA_COPY[persona.key]?.action ?? `Sign in as ${persona.accountType.toLowerCase()}`
}

export default function LoginPage() {
  const { login, loginSandboxPersona } = useAuth()
  const navigate = useNavigate()
  const emailSigninRef = useRef<HTMLDivElement | null>(null)
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [sandboxLoading, setSandboxLoading] = useState(false)
  const [sandboxEnabled, setSandboxEnabled] = useState(false)
  const [sandboxPersonas, setSandboxPersonas] = useState<SandboxDemoPersona[]>([])

  useEffect(() => {
    let cancelled = false
    api.auth.sandboxConfig()
      .then((res) => {
        if (cancelled) return
        setSandboxEnabled(Boolean(res.data?.enabled))
        setSandboxPersonas(Array.isArray(res.data?.personas) ? res.data.personas : [])
      })
      .catch(() => {
        if (cancelled) return
        setSandboxEnabled(false)
        setSandboxPersonas([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const visiblePersonas = useMemo(
    () => sandboxPersonas.filter((persona) => Boolean(persona.key)),
    [sandboxPersonas]
  )

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const user = await login(email.trim(), password)
      navigate(getDefaultRouteForUser(user), { replace: true })
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? 'Login failed. Check your credentials and try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const openCustomerDemo = () => {
    setError(null)
    navigate('/q/wahatoya-auto-group')
  }

  const scrollToEmailSignin = () => {
    emailSigninRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    window.setTimeout(() => emailInputRef.current?.focus(), 150)
  }

  const handleSandboxPersonaLogin = async (personaKey: string) => {
    setError(null)
    setSandboxLoading(true)
    try {
      const user = await loginSandboxPersona(personaKey)
      navigate(getDefaultRouteForUser(user), { replace: true })
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? 'Sandbox persona login is unavailable in this environment.'
      setError(msg)
    } finally {
      setSandboxLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -left-28 top-10 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <section className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-8">
            <div className="max-w-xl space-y-5">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-cyan-200/80">Diamond Dispatch</p>
                <h1 className="mt-3 text-4xl font-semibold tracking-tight text-white sm:text-5xl">Choose a demo role.</h1>
                <p className="mt-4 text-sm leading-6 text-slate-300 sm:text-base">
                  Sign in with secure, server-issued sandbox personas when local sandbox mode is enabled. Email/password remains available as the standard fallback.
                </p>
              </div>

              {error && (
                <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                  {error}
                </div>
              )}

              {sandboxEnabled && visiblePersonas.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {visiblePersonas.map((persona) => (
                    <button
                      key={persona.key}
                      type="button"
                      disabled={sandboxLoading}
                      onClick={() => handleSandboxPersonaLogin(persona.key)}
                      className="group rounded-2xl border border-white/10 bg-slate-900/70 p-4 text-left transition hover:border-cyan-400/40 hover:bg-slate-900 disabled:opacity-60"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-base font-semibold text-white">{personaLabel(persona)}</div>
                          <div className="mt-1 text-sm text-slate-400">{personaDescription(persona)}</div>
                          <div className="mt-2 text-xs uppercase tracking-wide text-slate-500">{persona.orgName}</div>
                        </div>
                        <div className="mt-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-cyan-100 transition group-hover:border-cyan-400/40">
                          {personaAction(persona)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!sandboxEnabled && (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                  Sandbox persona login is hidden because this environment is not verified for local sandbox mode.
                </div>
              )}

              <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-4 text-sm text-slate-300">
                Customer opens the public QuotePort form at <span className="font-semibold text-cyan-100">/q/wahatoya-auto-group</span>. No login is required for that flow.
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={openCustomerDemo}
                    className="rounded-full border border-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cyan-100 transition hover:border-cyan-300/40 hover:text-white"
                  >
                    Open Customer Demo
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={scrollToEmailSignin}
                  className="rounded-full border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-50 transition hover:bg-cyan-400/15"
                >
                  Sign in with Email
                </button>
                <button
                  type="button"
                  onClick={scrollToEmailSignin}
                  className="text-sm font-medium text-cyan-100 underline decoration-cyan-300/60 underline-offset-4 hover:text-white"
                >
                  Admin / Owner Sign In
                </button>
              </div>

            </div>
          </section>

          <aside ref={emailSigninRef} id="email-signin" className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl shadow-black/30 sm:p-8">
            <div className="text-center">
              <h2 className="mb-1 text-2xl font-bold text-slate-900">Sign in with email</h2>
              <p className="text-sm text-slate-500">Fallback for direct account access</p>
            </div>

            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  ref={emailInputRef}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm text-slate-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
                  placeholder="you@auxuniverse.com"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm text-slate-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </aside>
        </div>
      </div>
    </div>
  )
}
